import styles from '../styles.module.scss';
import { useCreateBlogsFilesMutation } from './vhc-record/api/create-blogs-file';
import { useWaitingStore } from './vhc-record/waiting-context';
import { Loading } from 'components/loading';
import { InlineSvg } from 'components/svg';
import { usePreviewImageContext } from 'features/vhc/shared/preview-image/preview-image-context';
import { useEffect, useRef } from 'react';

interface ImageUploaderProps {
  photos: string[];
  handleSetPhotos: (photos: string[]) => void;
  onRemove?: (index: number) => void;
  disabled?: boolean;
}

export const ImageUploader = ({ photos, handleSetPhotos, onRemove, disabled }: ImageUploaderProps) => {
  const { isWaiting, setIsWaiting } = useWaitingStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showPreviewImage } = usePreviewImageContext();

  const { mutateAsync: uploadFile, isLoading: isUploading } = useCreateBlogsFilesMutation();

  useEffect(() => {
    setIsWaiting(isUploading);
  }, [isUploading]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      Array.from(files).forEach((file) => {
        handleFileUpload(file);
      });
    }
  };

  const handleFileUpload = (file: File) => {
    if (file.type.startsWith('image/')) {
      uploadFile(
        {
          formData: {
            File: file
          }
        },
        {
          onSuccess: (response) => {
            if (response) {
              const newPhotos = [...photos, response];
              handleSetPhotos(newPhotos);
            }
          }
        }
      );
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemovePhoto = (index: number) => {
    if (onRemove) {
      onRemove(index);
    } else {
      const newPhotos = [...photos];
      newPhotos.splice(index, 1);
      handleSetPhotos(newPhotos);
    }
  };

  const handleOpenPreview = (index: number) => {
    showPreviewImage(
      photos.map((x) => ({
        fileUrl: x,
        isVideo: false
      })),
      index
    );
  };

  return (
    <div className={styles.imagesContainer}>
      <div className={styles.imagesActions}>
        {!disabled && (
          <button
            type="button"
            className="btn btn-sm btn-light-danger mb-3"
            onClick={handleButtonClick}
            disabled={disabled || isWaiting}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="svg-icon-2 me-1"
            >
              <g clipPath="url(#clip0_10165_33789)">
                <path
                  d="M15.219 6.31953H12.2815C11.9966 6.31095 11.7163 6.24496 11.4575 6.12551C11.1987 6.00606 10.9666 5.83562 10.7752 5.62437C10.5838 5.41313 10.437 5.16544 10.3436 4.89613C10.2502 4.62682 10.212 4.34142 10.2315 4.05703V1.05078L15.219 6.31953Z"
                  fill="currentColor"
                />
                <path
                  d="M4.05781 9.875C3.38151 9.875 2.73291 10.1437 2.25469 10.6219C1.77647 11.1001 1.50781 11.7487 1.50781 12.425C1.50781 13.1013 1.77647 13.7499 2.25469 14.2281C2.73291 14.7063 3.38151 14.975 4.05781 14.975C4.73411 14.975 5.38272 14.7063 5.86093 14.2281C6.33915 13.7499 6.60781 13.1013 6.60781 12.425C6.60781 11.7487 6.33915 11.1001 5.86093 10.6219C5.38272 10.1437 4.73411 9.875 4.05781 9.875ZM4.98906 12.9125H4.52656V13.3688C4.52339 13.4309 4.50787 13.4918 4.48092 13.5479C4.45396 13.604 4.41611 13.6541 4.36957 13.6954C4.32303 13.7367 4.26873 13.7684 4.20984 13.7885C4.15095 13.8086 4.08865 13.8167 4.02656 13.8125C3.90324 13.8093 3.78584 13.7589 3.69861 13.6717C3.61138 13.5845 3.56098 13.4671 3.55781 13.3437V12.8875H3.11406C3.04974 12.8851 2.98654 12.8699 2.92822 12.8427C2.86989 12.8154 2.81762 12.7768 2.7745 12.729C2.73138 12.6812 2.69828 12.6253 2.67716 12.5645C2.65604 12.5037 2.64734 12.4392 2.65156 12.375C2.65773 12.2516 2.70994 12.135 2.79788 12.0482C2.88582 11.9614 3.0031 11.9108 3.12656 11.9062H3.58281V11.45C3.59626 11.3326 3.65241 11.2243 3.74059 11.1456C3.82876 11.067 3.94278 11.0235 4.06094 11.0235C4.17909 11.0235 4.29312 11.067 4.38129 11.1456C4.46946 11.2243 4.52562 11.3326 4.53906 11.45V11.9062H4.99531C5.05741 11.9071 5.11872 11.9202 5.1757 11.9449C5.23268 11.9696 5.28417 12.0054 5.3272 12.0502C5.37024 12.0949 5.40394 12.1478 5.42636 12.2057C5.44878 12.2636 5.45947 12.3254 5.45781 12.3875C5.46296 12.4524 5.45469 12.5177 5.43353 12.5793C5.41237 12.6409 5.37876 12.6975 5.3348 12.7456C5.29084 12.7937 5.23746 12.8322 5.17798 12.8587C5.1185 12.8853 5.0542 12.8993 4.98906 12.9V12.9125Z"
                  fill="currentColor"
                />
                <path
                  opacity="0.3"
                  d="M15.2299 7.27656H12.2861C11.8776 7.26541 11.4755 7.17277 11.1032 7.00408C10.731 6.83539 10.3962 6.59406 10.1185 6.29424C9.84081 5.99442 9.62579 5.64215 9.48608 5.25811C9.34636 4.87406 9.28476 4.46598 9.30489 4.05781V1.03906H6.24239C5.3036 1.0506 4.40776 1.4342 3.75154 2.10565C3.09533 2.7771 2.73238 3.68151 2.74239 4.62031V9.99531C2.74239 10.0766 2.74239 10.1641 2.74239 10.2453C3.13771 9.99891 3.59535 9.87094 4.06114 9.87656C4.39369 9.8765 4.72292 9.94278 5.02954 10.0715C5.33616 10.2003 5.61401 10.3889 5.84682 10.6264C6.07963 10.8639 6.26273 11.1454 6.38538 11.4545C6.50804 11.7636 6.56779 12.0941 6.56114 12.4266C6.55707 12.8312 6.45862 13.2292 6.27364 13.5891H11.7486C12.6874 13.5775 13.5833 13.1939 14.2395 12.5225C14.8957 11.851 15.2586 10.9466 15.2486 10.0078L15.2299 7.27656Z"
                  fill="currentColor"
                />
              </g>
              <defs>
                <clipPath id="clip0_10165_33789">
                  <rect width="15" height="15" fill="white" transform="translate(0.898438 0.5)" />
                </clipPath>
              </defs>
            </svg>
            Upload more
          </button>
        )}
      </div>

      <div className={styles.imagesGrid}>
        {photos && photos.length > 0 ? (
          photos.map((photo, index) => (
            <div key={index} className={styles.photoThumb}>
              <img
                src={photo}
                alt={`Photo ${index + 1}`}
                className="svg-icon-2"
                onClick={() => handleOpenPreview(index)}
              />
              {!disabled && (
                <button
                  type="button"
                  className={`${styles.removeBtn}`}
                  onClick={() => handleRemovePhoto(index)}
                  disabled={disabled}
                >
                  <InlineSvg path={'/media/icons/duotune/arrows/arr011.svg'} />
                </button>
              )}
            </div>
          ))
        ) : (
          <div className="text-center w-100 py-4 text-muted">No photos uploaded yet</div>
        )}
      </div>

      <input
        disabled={disabled}
        type="file"
        ref={fileInputRef}
        className={styles.fileInput}
        accept="image/*"
        onChange={handleFileChange}
        multiple
      />
      {isUploading && <Loading />}
    </div>
  );
};
